import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Users, UserCheck, UserX, Clock, ShieldCheck, KeyRound, Pencil,
  Trash2, Check, X, Loader2, Search, Filter, UserPlus, Copy, Gift,
} from "lucide-react";
import { api } from "../lib/api";

const STATUS_LABELS = {
  active:    { label: "نشط",       color: "bg-[#A3C4B0]/25 text-[#1F3A33] border-[#A3C4B0]/40" },
  pending:   { label: "بانتظار الموافقة", color: "bg-[#FFE7BA] text-[#7A4D00] border-[#E8B96E]/40" },
  suspended: { label: "موقوف",     color: "bg-[#FBE9E3] text-[#7A2516] border-[#C84C31]/30" },
};

const ROLE_LABELS = {
  admin: { label: "مسؤول",  color: "bg-[#1F3A33] text-white" },
  user:  { label: "مستخدم", color: "bg-[#1A1C19]/10 text-[#1A1C19]" },
};

export default function AdminUsersPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all|pending|active|suspended
  const [editing, setEditing] = useState(null); // user object
  const [deleting, setDeleting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // {email, temp_password, message}
  const [granting, setGranting] = useState(null); // user object for subscription grant
  const [busy, setBusy] = useState(null); // user id currently being mutated

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== "all" && u.status !== filter) return false;
      if (!q) return true;
      return (u.email || "").toLowerCase().includes(q) ||
             (u.name  || "").toLowerCase().includes(q);
    });
  }, [users, search, filter]);

  const updateUser = async (userId, patch, successMsg) => {
    setBusy(userId);
    try {
      const res = await api.patch(`/admin/users/${userId}`, patch);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...res.data } : u)));
      toast.success(successMsg || "تم التحديث");
      await refreshStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحديث");
    } finally {
      setBusy(null);
    }
  };

  const refreshStats = async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const resetPassword = async (userId) => {
    setBusy(userId);
    try {
      const res = await api.post(`/admin/users/${userId}/reset-password`);
      toast.success(res.data?.message || `كلمة المرور المؤقتة: ${res.data?.temp_password}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة تعيين كلمة المرور");
    } finally {
      setBusy(null);
    }
  };

  const deleteUser = async (user) => {
    setBusy(user.id);
    try {
      await api.delete(`/admin/users/${user.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setDeleting(null);
      toast.success("تم حذف المستخدم وكافة فواتيره");
      await refreshStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحذف");
    } finally {
      setBusy(null);
    }
  };

  const createUser = async (payload) => {
    setBusy("__create__");
    try {
      const res = await api.post("/admin/users", payload);
      setUsers((prev) => [res.data, ...prev]);
      await refreshStats();
      setCreating(false);
      if (res.data.temp_password) {
        setCreatedInfo({
          email: res.data.email,
          temp_password: res.data.temp_password,
          message: res.data.message,
        });
      } else {
        toast.success(res.data.message || "تم إنشاء المستخدم");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إنشاء المستخدم");
    } finally {
      setBusy(null);
    }
  };

  const grantSubscription = async (userId, payload) => {
    setBusy(userId);
    try {
      const res = await api.post(`/admin/users/${userId}/subscription`, payload);
      toast.success(res.data?.message || "تم تحديث الاشتراك");
      setGranting(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحديث الاشتراك");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="admin-users-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">لوحة مسؤول النظام</div>
          <h1 className="font-readex font-semibold text-2xl sm:text-3xl text-[#1A1C19]">إدارة المستخدمين</h1>
          <p className="text-[#5C605A] mt-1 text-sm">تفعيل، إيقاف، تعديل وإعادة تعيين كلمات المرور لكل المستخدمين.</p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="open-create-user-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#1F3A33] text-white hover:bg-[#172A25] font-readex font-medium text-sm shadow-3d-sm"
        >
          <UserPlus className="w-4 h-4" strokeWidth={1.75} />
          إضافة مستخدم
        </button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users}      label="إجمالي المستخدمين"  value={stats?.total_users}     accent="bg-[#1F3A33]" testid="stat-total" />
        <StatCard icon={UserCheck}  label="نشطون"               value={stats?.active_users}    accent="bg-[#6A8A76]" testid="stat-active" />
        <StatCard icon={Clock}      label="بانتظار الموافقة"    value={stats?.pending_users}   accent="bg-[#E8B96E]" testid="stat-pending" />
        <StatCard icon={UserX}      label="موقوفون"             value={stats?.suspended_users} accent="bg-[#C84C31]" testid="stat-suspended" />
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-lg p-4 flex flex-wrap items-center gap-3 shadow-3d-sm">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-[#8A8D86]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالبريد أو الاسم…"
            data-testid="admin-search-input"
            className="w-full bg-[#F5F4EF] border border-transparent rounded-md ps-9 pe-4 py-2 text-sm focus:outline-none focus:bg-white focus:border-[#1F3A33]/25"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#8A8D86]" />
          {["all", "pending", "active", "suspended"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}-btn`}
              className={`px-3 py-1.5 rounded-full text-xs font-readex transition-colors ${
                filter === f
                  ? "bg-[#1F3A33] text-white"
                  : "bg-[#F5F4EF] text-[#5C605A] hover:bg-[#1A1C19]/5"
              }`}
            >
              {f === "all" ? "الكل" : STATUS_LABELS[f].label}
            </button>
          ))}
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-lg overflow-hidden shadow-3d-sm">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-[#5C605A]">
            <Loader2 className="w-5 h-5 animate-spin me-2" />
            جاري التحميل…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[#8A8D86] font-readex">لا يوجد مستخدمون يطابقون الفلاتر</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="bg-[#F5F4EF] text-[#5C605A] text-xs uppercase font-readex">
                  <th className="px-4 py-3 text-right">المستخدم</th>
                  <th className="px-4 py-3 text-center">الدور</th>
                  <th className="px-4 py-3 text-center">الحالة</th>
                  <th className="px-4 py-3 text-center">الفواتير</th>
                  <th className="px-4 py-3 text-center">تاريخ التسجيل</th>
                  <th className="px-4 py-3 text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    busy={busy === u.id}
                    onActivate={() => updateUser(u.id, { status: "active" }, "تم تفعيل المستخدم")}
                    onSuspend={()  => updateUser(u.id, { status: "suspended" }, "تم إيقاف المستخدم")}
                    onEdit={()     => setEditing(u)}
                    onResetPwd={() => resetPassword(u.id)}
                    onDelete={()   => setDeleting(u)}
                    onGrant={()    => setGranting(u)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditUserModal
          user={editing}
          busy={busy === editing.id}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await updateUser(editing.id, patch, "تم حفظ التغييرات");
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <ConfirmDeleteModal
          user={deleting}
          busy={busy === deleting.id}
          onCancel={() => setDeleting(null)}
          onConfirm={() => deleteUser(deleting)}
        />
      )}

      {creating && (
        <CreateUserModal
          busy={busy === "__create__"}
          onClose={() => setCreating(false)}
          onCreate={createUser}
        />
      )}

      {granting && (
        <GrantSubscriptionModal
          user={granting}
          busy={busy === granting.id}
          onClose={() => setGranting(null)}
          onGrant={(payload) => grantSubscription(granting.id, payload)}
        />
      )}

      {createdInfo && (
        <CredentialsModal
          info={createdInfo}
          onClose={() => setCreatedInfo(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#1A1C19]/10 rounded-lg p-4 shadow-3d-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-md ${accent} text-white flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="text-3xl font-readex font-semibold text-[#1A1C19] font-mono-num">
          {value ?? "—"}
        </div>
      </div>
      <div className="text-xs text-[#5C605A] mt-3 font-readex">{label}</div>
    </div>
  );
}

function UserRow({ user, busy, onActivate, onSuspend, onEdit, onResetPwd, onDelete, onGrant }) {
  const role = ROLE_LABELS[user.role] || ROLE_LABELS.user;
  const status = STATUS_LABELS[user.status] || STATUS_LABELS.active;
  const created = user.created_at ? new Date(user.created_at).toLocaleDateString("ar-SA-u-nu-latn") : "—";

  return (
    <tr className="border-t border-[#1A1C19]/8 hover:bg-[#F5F4EF]/40 transition-colors" data-testid={`user-row-${user.id}`}>
      <td className="px-4 py-3 text-right">
        <div className="font-readex font-medium text-[#1A1C19]">{user.name || "—"}</div>
        <div className="text-xs text-[#5C605A]" dir="ltr">{user.email}</div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-readex ${role.color}`}>
          {user.role === "admin" && <ShieldCheck className="w-3 h-3" />}
          {role.label}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs border font-readex ${status.color}`} data-testid={`status-${user.id}`}>
          {status.label}
        </span>
      </td>
      <td className="px-4 py-3 text-center font-mono-num text-[#5C605A]">{user.invoices_count ?? 0}</td>
      <td className="px-4 py-3 text-center text-xs text-[#5C605A] font-mono-num">{created}</td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1 flex-wrap">
          {user.status !== "active" && (
            <ActionBtn icon={Check} label="تفعيل" tone="green" onClick={onActivate} disabled={busy} testid={`activate-${user.id}`} />
          )}
          {user.status === "active" && user.role !== "admin" && (
            <ActionBtn icon={X} label="إيقاف" tone="red" onClick={onSuspend} disabled={busy} testid={`suspend-${user.id}`} />
          )}
          <ActionBtn icon={Pencil}  label="تعديل" tone="neutral" onClick={onEdit}     disabled={busy} testid={`edit-${user.id}`} />
          {user.role !== "admin" && (
            <ActionBtn icon={Gift} label="اشتراك" tone="green" onClick={onGrant} disabled={busy} testid={`grant-sub-${user.id}`} />
          )}
          <ActionBtn icon={KeyRound} label="إعادة كلمة المرور" tone="amber" onClick={onResetPwd} disabled={busy} testid={`reset-pwd-${user.id}`} />
          {user.role !== "admin" && (
            <ActionBtn icon={Trash2} label="حذف" tone="red" onClick={onDelete} disabled={busy} testid={`delete-${user.id}`} />
          )}
        </div>
      </td>
    </tr>
  );
}

const TONES = {
  green:   "bg-[#A3C4B0]/25 text-[#1F3A33] hover:bg-[#A3C4B0]/45",
  red:     "bg-[#FBE9E3] text-[#7A2516] hover:bg-[#F4D2C8]",
  amber:   "bg-[#FFE7BA] text-[#7A4D00] hover:bg-[#FFD58A]",
  neutral: "bg-[#1A1C19]/5 text-[#1A1C19] hover:bg-[#1A1C19]/10",
};

function ActionBtn({ icon: Icon, label, tone, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-readex transition-colors disabled:opacity-50 ${TONES[tone] || TONES.neutral}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      {label}
    </button>
  );
}

function EditUserModal({ user, busy, onClose, onSave }) {
  const [name, setName] = useState(user.name || "");  const [email, setEmail] = useState(user.email || "");
  const [status, setStatus] = useState(user.status || "active");
  const [role, setRole] = useState(user.role || "user");

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl" data-testid="edit-user-modal">
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-[#1A1C19]/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1C19]/10">
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">تعديل بيانات المستخدم</h3>
          <button onClick={onClose} className="text-[#5C605A] hover:text-[#1A1C19]" aria-label="إغلاق">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="الاسم">
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none" data-testid="edit-name-input" />
          </Field>
          <Field label="البريد الإلكتروني">
            <input value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none" data-testid="edit-email-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="الدور">
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none" data-testid="edit-role-select">
                <option value="user">مستخدم</option>
                <option value="admin">مسؤول</option>
              </select>
            </Field>
            <Field label="الحالة">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none" data-testid="edit-status-select">
                <option value="active">نشط</option>
                <option value="pending">بانتظار الموافقة</option>
                <option value="suspended">موقوف</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#1A1C19]/10 bg-[#FAF9F5]">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-[#5C605A] hover:bg-[#1A1C19]/5 font-readex" data-testid="edit-cancel-btn">
            إلغاء
          </button>
          <button
            onClick={() => {
              const patch = {};
              if (name !== user.name) patch.name = name;
              if (email !== user.email) patch.email = email;
              if (status !== user.status) patch.status = status;
              if (role !== user.role) patch.role = role;
              onSave(patch);
            }}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 font-readex"
            data-testid="edit-save-btn"
          >
            {busy ? "جاري الحفظ…" : "حفظ"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ user, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl" data-testid="delete-user-modal">
      <div className="bg-white rounded-lg w-full max-w-sm shadow-2xl border border-[#1A1C19]/10">
        <div className="p-5">
          <div className="w-12 h-12 rounded-full bg-[#FBE9E3] text-[#C84C31] flex items-center justify-center mb-3">
            <Trash2 className="w-5 h-5" />
          </div>
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">تأكيد حذف المستخدم</h3>
          <p className="text-sm text-[#5C605A] mt-2">
            سيتم حذف <span className="font-medium text-[#1A1C19]" dir="ltr">{user.email}</span> وجميع فواتيره بشكل نهائي. هذه العملية لا يمكن التراجع عنها.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#1A1C19]/10 bg-[#FAF9F5]">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm text-[#5C605A] hover:bg-[#1A1C19]/5 font-readex" data-testid="delete-cancel-btn">
            إلغاء
          </button>
          <button onClick={onConfirm} disabled={busy} className="px-4 py-2 rounded-md text-sm bg-[#C84C31] text-white hover:bg-[#B23F26] disabled:opacity-60 font-readex" data-testid="delete-confirm-btn">
            {busy ? "جاري الحذف…" : "حذف نهائي"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ busy, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [status, setStatus] = useState("active");
  const [useCustomPwd, setUseCustomPwd] = useState(false);
  const [customPwd, setCustomPwd] = useState("");

  const submit = () => {
    if (!email.trim()) {
      toast.error("يرجى إدخال البريد الإلكتروني");
      return;
    }
    if (useCustomPwd && customPwd.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    onCreate({
      name: name.trim() || undefined,
      email: email.trim().toLowerCase(),
      role,
      status,
      password: useCustomPwd ? customPwd : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl" data-testid="create-user-modal">
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-[#1A1C19]/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1C19]/10">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#1F3A33]" />
            <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">إضافة مستخدم جديد</h3>
          </div>
          <button onClick={onClose} className="text-[#5C605A] hover:text-[#1A1C19]" aria-label="إغلاق">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="الاسم">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="مثال: محطة إطفاء مدن الخرج"
              data-testid="create-name-input"
              className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none"
            />
          </Field>
          <Field label="البريد الإلكتروني *">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              dir="ltr"
              placeholder="user@example.com"
              data-testid="create-email-input"
              className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="الدور">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="create-role-select"
                className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none"
              >
                <option value="user">مستخدم</option>
                <option value="admin">مسؤول</option>
              </select>
            </Field>
            <Field label="الحالة">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="create-status-select"
                className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none"
              >
                <option value="active">نشط</option>
                <option value="pending">بانتظار الموافقة</option>
                <option value="suspended">موقوف</option>
              </select>
            </Field>
          </div>

          <div className="border-t border-[#1A1C19]/8 pt-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomPwd}
                onChange={(e) => setUseCustomPwd(e.target.checked)}
                data-testid="create-custom-pwd-checkbox"
                className="mt-1 accent-[#1F3A33] w-4 h-4 cursor-pointer"
              />
              <div className="text-sm text-[#1A1C19] font-readex">
                تعيين كلمة مرور مخصصة
                <div className="text-xs text-[#8A8D86] mt-0.5">
                  إذا تركتها فارغة، سيتم استخدام كلمة المرور المؤقتة <code className="bg-[#F5F4EF] px-1 rounded">12345678</code> ويُجبر المستخدم على تغييرها عند أول دخول.
                </div>
              </div>
            </label>
            {useCustomPwd && (
              <input
                type="text"
                value={customPwd}
                onChange={(e) => setCustomPwd(e.target.value)}
                dir="ltr"
                placeholder="كلمة المرور (6 أحرف على الأقل)"
                data-testid="create-custom-pwd-input"
                className="mt-2 w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none font-mono-num"
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#1A1C19]/10 bg-[#FAF9F5]">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-[#5C605A] hover:bg-[#1A1C19]/5 font-readex" data-testid="create-cancel-btn">
            إلغاء
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="create-submit-btn"
            className="px-4 py-2 rounded-md text-sm bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 font-readex font-medium"
          >
            {busy ? "جاري الإنشاء…" : "إنشاء الحساب"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CredentialsModal({ info, onClose }) {
  const copy = () => {
    const text = `البريد: ${info.email}\nكلمة المرور المؤقتة: ${info.temp_password}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success("تم نسخ بيانات الدخول"),
      () => toast.error("تعذّر النسخ — انسخ يدوياً"),
    );
  };
  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl" data-testid="credentials-modal">
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-[#1A1C19]/10">
        <div className="p-5">
          <div className="w-12 h-12 rounded-full bg-[#E8F0EA] text-[#1F3A33] flex items-center justify-center mb-3">
            <Check className="w-6 h-6" strokeWidth={2} />
          </div>
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">تم إنشاء الحساب بنجاح</h3>
          <p className="text-sm text-[#5C605A] mt-2 leading-relaxed">
            شارك بيانات الدخول التالية مع المستخدم. سيُطلب منه تغيير كلمة المرور عند أول دخول.
          </p>
          <div className="mt-4 bg-[#F5F4EF] rounded-md p-4 space-y-2 border border-[#1A1C19]/8">
            <div className="flex justify-between items-center gap-3">
              <span className="text-xs text-[#5C605A] font-readex">البريد:</span>
              <span className="font-mono-num text-sm text-[#1A1C19]" dir="ltr" data-testid="cred-email">{info.email}</span>
            </div>
            <div className="flex justify-between items-center gap-3">
              <span className="text-xs text-[#5C605A] font-readex">كلمة المرور المؤقتة:</span>
              <span className="font-mono-num text-base font-semibold text-[#1F3A33] bg-white px-2 py-0.5 rounded border border-[#1F3A33]/15" dir="ltr" data-testid="cred-password">{info.temp_password}</span>
            </div>
          </div>
          <button
            onClick={copy}
            data-testid="copy-creds-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm bg-[#1A1C19]/5 hover:bg-[#1A1C19]/10 text-[#1A1C19] font-readex"
          >
            <Copy className="w-4 h-4" /> نسخ البيانات
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#1A1C19]/10 bg-[#FAF9F5]">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm bg-[#1F3A33] text-white hover:bg-[#172A25] font-readex" data-testid="cred-done-btn">
            تم
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[#5C605A] mb-1.5 font-readex">{label}</div>
      {children}
    </label>
  );
}

const GRANT_PLANS = [
  { id: "monthly",    label: "شهري (شهر واحد)" },
  { id: "quarterly",  label: "ربع سنوي (3 أشهر)" },
  { id: "semiannual", label: "نصف سنوي (6 أشهر)" },
  { id: "annual",     label: "سنوي (12 شهرًا)" },
];

function GrantSubscriptionModal({ user, busy, onClose, onGrant }) {
  const [mode, setMode] = useState("plan"); // plan | months
  const [planId, setPlanId] = useState("quarterly");
  const [months, setMonths] = useState("1");
  const sub = user.subscription;
  const subEnd = sub?.end_date ? new Date(sub.end_date) : null;
  const subActive = subEnd && subEnd > new Date();

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl" data-testid="grant-sub-modal">
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-[#1A1C19]/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1A1C19]/10">
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">إدارة اشتراك المستخدم</h3>
          <button onClick={onClose} className="text-[#5C605A] hover:text-[#1A1C19]" aria-label="إغلاق">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#5C605A] font-readex">
            المستخدم: <span className="text-[#1A1C19] font-medium">{user.name || user.email}</span>
          </div>
          <div className="bg-[#F5F4EF] rounded-md px-4 py-3 text-sm font-readex" data-testid="grant-current-sub">
            {sub ? (
              <>
                <span className="text-[#5C605A]">الاشتراك الحالي: </span>
                <span className="text-[#1A1C19] font-medium">{sub.plan_name_ar || sub.plan_id}</span>
                <span className={`ms-2 ${subActive ? "text-[#1F3A33]" : "text-[#C84C31]"}`}>
                  ({subActive ? "نشط" : "منتهي"} — حتى {subEnd ? subEnd.toLocaleDateString("ar-SA-u-nu-latn") : "—"})
                </span>
              </>
            ) : (
              <span className="text-[#8A8D86]">لا يوجد اشتراك بعد</span>
            )}
          </div>

          <div className="flex gap-2">
            {[["plan", "خطة جاهزة"], ["months", "أشهر مخصصة"]].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)}
                className={`px-3 py-1.5 rounded-full text-xs font-readex ${mode === m ? "bg-[#1F3A33] text-white" : "bg-[#F5F4EF] text-[#5C605A]"}`}
                data-testid={`grant-mode-${m}`}>
                {label}
              </button>
            ))}
          </div>

          {mode === "plan" ? (
            <Field label="الخطة">
              <select value={planId} onChange={(e) => setPlanId(e.target.value)}
                className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none"
                data-testid="grant-plan-select">
                {GRANT_PLANS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="عدد الأشهر">
              <input type="number" min="1" max="60" value={months} onChange={(e) => setMonths(e.target.value)}
                className="w-full bg-[#F5F4EF] rounded-md px-3 py-2 border border-transparent focus:border-[#1F3A33]/30 focus:bg-white outline-none font-mono-num"
                data-testid="grant-months-input" />
            </Field>
          )}
          <p className="text-xs text-[#8A8D86]">إذا كان لدى المستخدم اشتراك نشط سيتم تمديده، وإلا يبدأ اشتراك جديد من اليوم.</p>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-[#1A1C19]/10 bg-[#FAF9F5]">
          {subActive ? (
            <button onClick={() => onGrant({ action: "cancel" })} disabled={busy}
              className="px-4 py-2 rounded-md text-sm text-[#C84C31] hover:bg-[#C84C31]/10 font-readex disabled:opacity-50"
              data-testid="grant-cancel-sub-btn">
              إلغاء الاشتراك
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-[#5C605A] hover:bg-[#1A1C19]/5 font-readex">
              إغلاق
            </button>
            <button
              onClick={() => onGrant(mode === "plan" ? { action: "grant", plan_id: planId } : { action: "grant", months: Number(months) })}
              disabled={busy || (mode === "months" && (!months || Number(months) < 1))}
              className="px-4 py-2 rounded-md text-sm bg-[#1F3A33] text-white hover:bg-[#172A25] font-readex disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="grant-confirm-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              منح / تمديد
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
